import { useState, useEffect, useRef } from 'react';
import { Horizon, Btn, Mo, Bd, Dp, Label } from './shared';
import { GOLD, EMBER, DEBIT_RED, MONO } from '../lib/constants';
import { CATEGORY_LABELS, GRADE_COLORS, TREND_COLORS, TREND_ARROWS, CAT_ICONS, Card, SL, BackBtn, fmtDate } from './InsightsShared';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { QuickFixScreen } from './QuickFixScreen';
import { ReclassifyTransactionModal } from './ReclassifyTransactionModal';
import { LiquidFillGauge } from './LiquidFillGauge';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Reclassification Options ── */
const CATEGORY_RECLASS_OPTIONS = [
  { id: 'food_drink', label: 'Food & Drink', icon: 'F', category: 'FOOD_AND_DRINK' },
  { id: 'entertainment', label: 'Entertainment', icon: 'E', category: 'ENTERTAINMENT' },
  { id: 'shopping', label: 'Shopping', icon: 'S', category: 'GENERAL_MERCHANDISE' },
  { id: 'transportation', label: 'Transportation', icon: 'T', category: 'TRANSPORTATION' },
  { id: 'rent_bills', label: 'Rent & Bills', icon: 'R', category: 'RENT_AND_UTILITIES' },
  { id: 'personal_care', label: 'Personal Care', icon: 'P', category: 'PERSONAL_CARE' },
  { id: 'travel', label: 'Travel', icon: 'Tr', category: 'TRAVEL' },
  { id: 'medical', label: 'Medical', icon: 'M', category: 'MEDICAL' },
  { id: 'services', label: 'Services', icon: 'Sv', category: 'GENERAL_SERVICES' },
  { id: 'home', label: 'Home', icon: 'H', category: 'HOME_IMPROVEMENT' },
  { id: 'gov_tax', label: 'Gov & Tax', icon: 'G', category: 'GOVERNMENT_AND_NON_PROFIT' },
  { id: 'other', label: 'Other', icon: 'O', category: 'OTHER' },
];

const SPECIAL_RECLASS_OPTIONS = [
  { id: 'subscriptions', label: 'Subscriptions', icon: 'Sub', category: 'SUBSCRIPTIONS', classification: 'subscriptions' },
  { id: 'vehicle_payment', label: 'Vehicle / Auto', icon: 'V', category: 'VEHICLE_AUTO', classification: 'vehicle_payment' },
  { id: 'debt_payment', label: 'Loan / Debt Payment', icon: 'D', category: 'LOAN_DEBT_PAYMENT', classification: 'debt_payment' },
  { id: 'insurance', label: 'Insurance', icon: 'In', category: 'INSURANCE', classification: 'insurance' },
  { id: 'investment', label: 'Investment', icon: 'Iv', category: 'INVESTMENTS', classification: 'investment' },
  { id: 'business_taxes', label: 'Business Taxes', icon: 'Bt', category: 'BUSINESS_TAXES', classification: 'business_taxes' },
];

const BUSINESS_RECLASS_OPTIONS = [
  { id: 'business_cogs', label: 'Business (COGS)', icon: 'Bc', classification: 'business_cogs' },
  { id: 'business_operations', label: 'Business Operations', icon: 'Bo', classification: 'business_operations' },
  { id: 'payroll_contractors', label: 'Payroll & Contractors', icon: 'Pc', classification: 'payroll_contractors' },
];

const SPENDING_RECLASS_OPTIONS = [
  ...CATEGORY_RECLASS_OPTIONS,
  ...SPECIAL_RECLASS_OPTIONS,
  ...BUSINESS_RECLASS_OPTIONS,
  { id: 'ignore', label: 'Ignore', icon: 'X', classification: 'ignore' },
];

const INCOME_RECLASS_OPTIONS = [
  { id: 'business_revenue', label: 'Business Revenue', icon: 'Br' },
  { id: 'salary', label: 'Personal Income', icon: 'Pi' },
  { id: 'loan_advance', label: 'Loan / Cash Advance', icon: 'La', hint: 'Not income' },
  { id: 'transfer', label: 'Transfer', icon: 'Tf' },
  { id: 'investment_return', label: 'Investment Return', icon: 'Ir' },
  { id: 'refund', label: 'Refund', icon: 'Rf' },
  { id: 'other', label: 'Other', icon: 'O' },
];

// All categories for dual reclassification modal - expanded taxonomy
const ALL_CATEGORIES = [
  // ── PERSONAL: Discretionary ──
  { value: 'FOOD_AND_DRINK',    label: 'Food & Drink',        type: 'personal', classification: 'personal' },
  { value: 'DINING_OUT',        label: 'Dining Out',          type: 'personal', classification: 'personal' },
  { value: 'ENTERTAINMENT',     label: 'Entertainment',        type: 'personal', classification: 'personal' },
  { value: 'SHOPPING',          label: 'Shopping',             type: 'personal', classification: 'personal' },
  { value: 'PERSONAL_CARE',     label: 'Personal Care',        type: 'personal', classification: 'personal' },
  { value: 'TRAVEL',            label: 'Travel',               type: 'personal', classification: 'personal' },
  { value: 'RECREATION',        label: 'Recreation',           type: 'personal', classification: 'personal' },
  { value: 'FITNESS',           label: 'Fitness',              type: 'personal', classification: 'personal' },
  { value: 'COFFEE_SHOPS',      label: 'Coffee Shops',         type: 'personal', classification: 'personal' },
  { value: 'PET_CARE',          label: 'Pet Care',             type: 'personal', classification: 'personal' },
  { value: 'BEAUTY_SALON',      label: 'Beauty & Salon',       type: 'personal', classification: 'personal' },
  { value: 'ALCOHOL_TOBACCO',   label: 'Alcohol & Tobacco',    type: 'personal', classification: 'personal' },
  // ── PERSONAL: Essential ──
  { value: 'GROCERIES',         label: 'Groceries',            type: 'personal', classification: 'personal' },
  { value: 'RENT_AND_UTILITIES',label: 'Rent & Bills',         type: 'personal', classification: 'personal' },
  { value: 'HOUSING',           label: 'Housing',              type: 'personal', classification: 'personal' },
  { value: 'TRANSPORTATION',    label: 'Transportation',        type: 'personal', classification: 'personal' },
  { value: 'GAS_FUEL',          label: 'Gas & Fuel',           type: 'personal', classification: 'personal' },
  { value: 'MEDICAL',           label: 'Medical',              type: 'personal', classification: 'personal' },
  { value: 'HEALTHCARE',        label: 'Healthcare',           type: 'personal', classification: 'personal' },
  { value: 'CHILDCARE',         label: 'Childcare',            type: 'personal', classification: 'personal' },
  { value: 'EDUCATION',         label: 'Education',            type: 'personal', classification: 'personal' },
  { value: 'HOME_IMPROVEMENT',  label: 'Home Improvement',     type: 'personal', classification: 'personal' },
  { value: 'PARKING_TOLLS',     label: 'Parking & Tolls',      type: 'personal', classification: 'personal' },
  // ── PERSONAL: Fixed ──
  { value: 'PHONE_SERVICE',     label: 'Phone Service',        type: 'personal', classification: 'personal' },
  { value: 'INTERNET',          label: 'Internet',             type: 'personal', classification: 'personal' },
  { value: 'SUBSCRIPTIONS',     label: 'Subscriptions',        type: 'personal', classification: 'subscriptions' },
  { value: 'STORAGE_UNIT',      label: 'Storage Unit',         type: 'personal', classification: 'personal' },
  // ── PERSONAL: Insurance ──
  { value: 'PERSONAL_INSURANCE',label: 'Personal Insurance',   type: 'personal', classification: 'insurance' },
  { value: 'CAR_INSURANCE',     label: 'Car Insurance',        type: 'personal', classification: 'insurance' },
  { value: 'HEALTH_INSURANCE',  label: 'Health Insurance',     type: 'personal', classification: 'insurance' },
  { value: 'LIFE_INSURANCE',    label: 'Life Insurance',       type: 'personal', classification: 'insurance' },
  // ── PERSONAL: Debt ──
  { value: 'LOAN_DEBT_PAYMENT',     label: 'Loan / Debt Payment',   type: 'personal', classification: 'debt_payment' },
  { value: 'CREDIT_CARD_PAYMENT',   label: 'Credit Card Payment',   type: 'personal', classification: 'debt_payment' },
  { value: 'AUTO_LOAN_PAYMENT',     label: 'Auto Loan Payment',     type: 'personal', classification: 'vehicle_payment' },
  { value: 'MORTGAGE_PAYMENT',      label: 'Mortgage Payment',      type: 'personal', classification: 'debt_payment' },
  { value: 'STUDENT_LOAN_PAYMENT',  label: 'Student Loan',          type: 'personal', classification: 'debt_payment' },
  { value: 'PERSONAL_LOAN_PAYMENT', label: 'Personal Loan',         type: 'personal', classification: 'debt_payment' },
  { value: 'MEDICAL_DEBT_PAYMENT',  label: 'Medical Debt',          type: 'personal', classification: 'debt_payment' },
  { value: 'BNPL_PAYMENT',          label: 'Buy Now Pay Later',     type: 'personal', classification: 'debt_payment' },
  // ── PERSONAL: Vehicle / Other ──
  { value: 'VEHICLE_AUTO',    label: 'Vehicle / Auto',   type: 'personal', classification: 'vehicle_payment' },
  { value: 'INVESTMENTS',     label: 'Investment',       type: 'personal', classification: 'investment' },
  { value: 'OTHER',           label: 'Other',            type: 'personal', classification: 'personal' },
  // ── BUSINESS: COGS ──
  { value: 'COST_OF_GOODS',         label: 'Cost of Goods (COGS)',        type: 'business', classification: 'business_cogs' },
  { value: 'SUPPLIES_EQUIPMENT',    label: 'Supplies & Equipment',        type: 'business', classification: 'business_operations' },
  { value: 'SHIPPING_DELIVERY',     label: 'Shipping & Delivery',         type: 'business', classification: 'business_operations' },
  // ── BUSINESS: Operations ──
  { value: 'BUSINESS_OPERATIONS',    label: 'Business Operations',        type: 'business', classification: 'business_operations' },
  { value: 'BUSINESS_RENT',          label: 'Business Rent',              type: 'business', classification: 'business_operations' },
  { value: 'BUSINESS_UTILITIES',     label: 'Business Utilities',         type: 'business', classification: 'business_operations' },
  { value: 'BUSINESS_SUBSCRIPTIONS', label: 'Business Software',          type: 'business', classification: 'business_operations' },
  { value: 'ADVERTISING_MARKETING',  label: 'Advertising & Marketing',    type: 'business', classification: 'business_operations' },
  { value: 'MERCHANT_FEES',          label: 'Merchant Fees',              type: 'business', classification: 'business_operations' },
  { value: 'PROFESSIONAL_SERVICES',  label: 'Professional Services',      type: 'business', classification: 'business_operations' },
  { value: 'POS_SYSTEM_FEES',        label: 'POS System Fees',            type: 'business', classification: 'business_operations' },
  { value: 'BUSINESS_PHONE_INTERNET',label: 'Business Phone & Internet',  type: 'business', classification: 'business_operations' },
  { value: 'BUSINESS_TRAVEL',        label: 'Business Travel',            type: 'business', classification: 'business_operations' },
  // ── BUSINESS: People ──
  { value: 'PAYROLL_CONTRACTORS',label: 'Payroll & Contractors',  type: 'business', classification: 'payroll_contractors' },
  { value: 'PAYROLL_WAGES',      label: 'Payroll & Wages',        type: 'business', classification: 'payroll_contractors' },
  { value: 'CONTRACTOR_PAYMENTS',label: 'Contractor Payments',    type: 'business', classification: 'payroll_contractors' },
  // ── BUSINESS: Taxes & Insurance ──
  { value: 'BUSINESS_TAXES',    label: 'Business Taxes',          type: 'business', classification: 'business_taxes' },
  { value: 'BUSINESS_INSURANCE',label: 'Business Insurance',      type: 'business', classification: 'business_operations' },
  // ── BUSINESS: Vehicle ──
  { value: 'VEHICLE_FLEET',label: 'Vehicle / Fleet',  type: 'business', classification: 'vehicle_payment' },
  // ── BUSINESS: Debt ──
  { value: 'MCA_REPAYMENT',  label: 'MCA Repayment',    type: 'business', classification: 'debt_payment' },
  { value: 'BUSINESS_LOAN',  label: 'Business Loan',    type: 'business', classification: 'debt_payment' },
  { value: 'SBA_LOAN',       label: 'SBA Loan',         type: 'business', classification: 'debt_payment' },
  { value: 'LINE_OF_CREDIT', label: 'Line of Credit',   type: 'business', classification: 'debt_payment' },
  // ── OTHER ──
  { value: 'TRANSFER', label: 'Transfer', type: 'other', classification: 'transfer' },
  { value: 'IGNORED',  label: 'Ignore',   type: 'spending', classification: 'ignore' },
];

// Helper to find option and build classification data
function buildClassificationData(optionId) {
  const allOptions = [...SPENDING_RECLASS_OPTIONS, ...INCOME_RECLASS_OPTIONS];
  const opt = allOptions.find(o => o.id === optionId);
  if (!opt) return { classification: optionId, type: 'spending' };
  
  // Determine type based on classification
  let type = 'personal';  // default
  const classification = opt.classification || opt.id;
  
  // Business classifications
  if (classification === 'business_cogs' || classification === 'business_operations' || 
      classification === 'business_taxes' || classification === 'payroll_contractors') {
    type = 'business';
  }
  // Income classifications
  else if (['business_revenue', 'salary', 'loan_advance', 'transfer', 'investment_return', 'refund', 'other'].includes(classification)) {
    type = 'income';
  }
  // Personal/special classifications (debt_payment, vehicle_payment, insurance, investment, subscriptions, etc.)
  else {
    type = 'personal';
  }
  
  const data = { type, reclassified_at: new Date().toISOString() };
  
  // If option has a direct category override
  if (opt.category) {
    data.category_override = opt.category;
    data.classification = classification;
  } else {
    data.classification = classification;
  }
  return data;
}

// Helper to get display label for a classification
function getClassificationLabel(classData) {
  if (!classData) return null;
  const catOverride = classData.category_override;
  const classId = classData.classification;
  
  if (catOverride) {
    return CATEGORY_LABELS[catOverride] || catOverride;
  }
  const opt = SPENDING_RECLASS_OPTIONS.find(o => o.id === classId || o.classification === classId);
  return opt?.label || classId;
}

/* ── Reclassification Dropdown ── */
function ReclassDropdown({ options, currentValue, onSelect, onClose }) {
  // Group options into categories
  const categoryOpts = options.filter(o => CATEGORY_RECLASS_OPTIONS.some(c => c.id === o.id));
  const specialOpts = options.filter(o => SPECIAL_RECLASS_OPTIONS.some(s => s.id === o.id));
  const businessOpts = options.filter(o => BUSINESS_RECLASS_OPTIONS.some(b => b.id === o.id));
  const otherOpts = options.filter(o => o.id === 'ignore');
  
  const renderOption = (opt) => (
    <button
      key={opt.id}
      onClick={() => { onSelect(opt.id); onClose(); }}
      data-testid={`reclass-option-${opt.id}`}
      style={{
        width: '100%', 
        padding: '10px 14px', 
        background: currentValue === opt.id ? `${GOLD}20` : '#1a1a1a',
        border: 'none', 
        cursor: 'pointer', 
        display: 'flex', 
        alignItems: 'center', 
        gap: 10,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <span style={{ 
        fontSize: 9, fontFamily: MONO, fontWeight: 700, letterSpacing: 1,
        width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: currentValue === opt.id ? `${GOLD}30` : 'rgba(255,255,255,0.06)',
        borderRadius: 6, color: currentValue === opt.id ? GOLD : 'var(--mute)',
      }}>{opt.icon}</span>
      <div style={{ flex: 1, textAlign: 'left' }}>
        <Mo style={{ fontSize: 11, color: currentValue === opt.id ? 'var(--gold)' : 'var(--light)', display: 'block' }}>{opt.label}</Mo>
        {opt.hint && <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block' }}>{opt.hint}</Mo>}
      </div>
      {currentValue === opt.id && (
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      )}
    </button>
  );
  
  const sectionLabel = (text) => (
    <div key={`section-${text}`} style={{ 
      padding: '8px 14px 4px', fontSize: 9, fontFamily: MONO, fontWeight: 700,
      color: 'var(--mute)', letterSpacing: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)',
    }}>{text}</div>
  );
  
  return (
    <div className="fade-in" style={{
      position: 'absolute', 
      right: 0, 
      top: '100%', 
      zIndex: 9999,
      background: '#1a1a1a',
      border: '1px solid rgba(255,255,255,0.15)', 
      borderRadius: 10,
      boxShadow: '0 12px 40px rgba(0,0,0,0.8)', 
      minWidth: 240, 
      maxHeight: 400, 
      overflowY: 'auto',
      overflowX: 'hidden',
    }}>
      {categoryOpts.length > 0 && sectionLabel('MOVE TO CATEGORY')}
      {categoryOpts.map(renderOption)}
      {specialOpts.length > 0 && sectionLabel('SPECIAL EXPENSE')}
      {specialOpts.map(renderOption)}
      {businessOpts.length > 0 && sectionLabel('BUSINESS')}
      {businessOpts.map(renderOption)}
      {otherOpts.length > 0 && otherOpts.map(renderOption)}
    </div>
  );
}

/* ── Edit Icon Button ── */
function EditIconBtn({ onClick, isActive }) {
  return (
    <button
      onClick={onClick}
      data-testid="edit-classification-btn"
      style={{
        background: isActive ? `${GOLD}20` : 'transparent',
        border: `1px solid ${isActive ? GOLD : 'var(--ghost)'}`,
        borderRadius: 6, padding: 6, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.2s ease',
      }}
    >
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={isActive ? GOLD : 'var(--mute)'} strokeWidth="2" strokeLinecap="round">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    </button>
  );
}

/* ── Floating Re-Analyze Button ── */
function FloatingReAnalyzeBtn({ visible, onClick, loading }) {
  if (!visible) return null;
  
  return (
    <div className="fade-in" style={{
      position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
      zIndex: 1000,
    }}>
      <button
        onClick={onClick}
        disabled={loading}
        data-testid="re-analyze-btn"
        style={{
          padding: '14px 28px', background: GOLD, border: 'none', borderRadius: 30,
          cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: `0 4px 20px ${GOLD}50`, transition: 'all 0.2s ease',
          opacity: loading ? 0.7 : 1,
        }}
      >
        {loading ? (
          <div style={{ width: 16, height: 16, border: '2px solid #000', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        ) : (
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round">
            <path d="M23 4v6h-6M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
          </svg>
        )}
        <Bd style={{ fontSize: 12, fontWeight: 700, color: '#000', letterSpacing: 1 }}>
          {loading ? 'RE-ANALYZING...' : 'RE-ANALYZE'}
        </Bd>
      </button>
    </div>
  );
}

/* ── Category Colors for Pie Chart ── */
const CATEGORY_COLORS = [
  '#F5A623', // Gold
  '#22c55e', // Green
  '#3b82f6', // Blue
  '#ef4444', // Red
  '#a855f7', // Purple
  '#ec4899', // Pink
  '#14b8a6', // Teal
  '#f97316', // Orange
  '#6366f1', // Indigo
  '#84cc16', // Lime
  '#06b6d4', // Cyan
  '#f43f5e', // Rose
];

/* ── Category Donut Chart ── */
function CategoryDonutChart({ categories }) {
  if (!categories || categories.length === 0) return null;
  
  const chartData = categories.slice(0, 8).map((cat, i) => ({
    name: cat.display_name || CATEGORY_LABELS[cat.name] || cat.name,
    value: cat.amount,
    fill: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0];
      return (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--ghost)', borderRadius: 8, padding: '8px 12px' }}>
          <Mo style={{ fontSize: 12, fontWeight: 600, color: 'var(--light)', display: 'block' }}>{d.name}</Mo>
          <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 4 }}>${d.value?.toLocaleString()}</Mo>
        </div>
      );
    }
    return null;
  };

  const renderLegend = (props) => {
    const { payload } = props;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 8 }}>
        {payload.map((entry, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color }} />
            <Mo style={{ fontSize: 9, color: 'var(--mute)' }}>{entry.value}</Mo>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="category-donut-chart" className="chart-entrance" style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="45%"
            innerRadius={50}
            outerRadius={80}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, i) => (
              <Cell key={`cell-${i}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend content={renderLegend} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── Monthly Trend Bar Chart ── */
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function MonthlyTrendChart({ data }) {
  if (!data || data.length < 2) return null;
  
  const chartData = data.map((d, i) => {
    const monthNum = parseInt(d.month.slice(5), 10); // Extract MM from YYYY-MM
    return {
      month: MONTH_NAMES[monthNum - 1] || d.month.slice(5),
      total: d.total,
      fill: i === data.length - 1 ? '#00FFCC' : '#4a5568', // Teal for current month
    };
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--ghost)', borderRadius: 8, padding: '8px 12px' }}>
          <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block' }}>{label}</Mo>
          <Mo style={{ fontSize: 13, fontWeight: 700, color: 'var(--light)', display: 'block', marginTop: 2 }}>${payload[0].value?.toLocaleString()}</Mo>
        </div>
      );
    }
    return null;
  };

  return (
    <div data-testid="monthly-trend-chart" className="chart-entrance" style={{ width: '100%', height: 180, marginTop: 12 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--faint)" vertical={false} />
          <XAxis 
            dataKey="month" 
            tick={{ fontSize: 10, fill: 'var(--mute)' }} 
            axisLine={{ stroke: 'var(--faint)' }}
            tickLine={false}
          />
          <YAxis 
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            tick={{ fontSize: 10, fill: 'var(--mute)' }}
            axisLine={false}
            tickLine={false}
            width={45}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--faint)' }} />
          <Bar dataKey="total" radius={[4, 4, 0, 0]}>
            {chartData.map((entry, i) => (
              <Cell key={`cell-${i}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── Top Merchants Horizontal Bar (with Reclassification) ── */
function TopMerchantsChart({ merchants, onReclassify, classifications, isBusiness }) {
  const [editingMerchant, setEditingMerchant] = useState(null);
  
  if (!merchants || merchants.length === 0) return null;
  
  const maxAmount = Math.max(...merchants.slice(0, 6).map(m => m.total));
  const options = SPENDING_RECLASS_OPTIONS;
  
  const handleReclassifyMerchant = (merchant, optionId) => {
    if (onReclassify) {
      onReclassify(merchant, optionId, 'merchant');
    }
    setEditingMerchant(null);
  };
  
  return (
    <div data-testid="top-merchants-chart" style={{ marginTop: 8 }}>
      {merchants.slice(0, 6).map((m, i) => {
        const pct = maxAmount > 0 ? (m.total / maxAmount) * 100 : 0;
        const classData = classifications?.[m.name];
        const classLabel = getClassificationLabel(classData);
        const isEditing = editingMerchant === m.name;
        
        return (
          <div key={i} style={{ 
            marginBottom: 12, 
            position: 'relative',
            zIndex: isEditing ? 1000 : 1,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0, overflow: 'visible' }}>
                <Bd style={{ fontSize: 12, color: 'var(--light)', display: 'block', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>{m.name}</Bd>
                <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{m.count} transactions</Mo>
              </div>
              <Mo style={{ fontSize: 12, fontWeight: 700, color: 'var(--light)', flexShrink: 0, whiteSpace: 'nowrap' }}>${m.total?.toLocaleString()}</Mo>
              {onReclassify && (
                <EditIconBtn 
                  onClick={() => setEditingMerchant(isEditing ? null : m.name)} 
                  isActive={isEditing || !!classLabel}
                />
              )}
            </div>
            <div style={{ height: 6, background: 'var(--faint)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ 
                height: '100%', 
                width: `${pct}%`, 
                background: `linear-gradient(90deg, ${CATEGORY_COLORS[i % CATEGORY_COLORS.length]}, ${CATEGORY_COLORS[(i + 1) % CATEGORY_COLORS.length]})`,
                borderRadius: 3,
                transition: 'width 0.5s ease'
              }} />
            </div>
            {classLabel && !isEditing && (
              <Mo style={{ fontSize: 8, color: 'var(--gold)', marginTop: 4 }}>
                Merchant rule: {classLabel}
              </Mo>
            )}
            {isEditing && (
              <ReclassDropdown
                options={options}
                currentValue={classData?.classification || classData?.category_override}
                onSelect={(cls) => handleReclassifyMerchant(m.name, cls)}
                onClose={() => setEditingMerchant(null)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Mini Bar Chart ── */
function MiniBarChart({ data }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data.map(d => d.total));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 48, marginTop: 8 }}>
      {data.map((d, i) => {
        const h = max > 0 ? (d.total / max) * 100 : 0;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div style={{ width: '100%', height: `${h}%`, background: i === data.length - 1 ? GOLD : 'var(--ghost)', borderRadius: 2, minHeight: 2, transition: 'height 0.5s' }} />
            <Mo style={{ fontSize: 7, color: 'var(--mute)' }}>{d.month.slice(5)}</Mo>
          </div>
        );
      })}
    </div>
  );
}

/* ── Personal vs Business Split Card ── */
function PersonalBusinessSplit({ data }) {
  if (!data.has_business_tracking) return null;
  
  const personalPct = data.total_spending > 0 ? (data.personal_spending / data.total_spending * 100) : 0;
  const businessPct = data.total_spending > 0 ? (data.business_spending / data.total_spending * 100) : 0;
  
  return (
    <Card testId="personal-business-split">
      <SL>SPENDING BREAKDOWN</SL>
      <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginBottom: 16 }}>
        Your account is used for both personal and business expenses
      </Mo>
      
      {/* Split Bar */}
      <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', marginBottom: 16 }}>
        <div style={{ width: `${personalPct}%`, background: GOLD, transition: 'width 0.5s' }} />
        <div style={{ width: `${businessPct}%`, background: '#22c55e', transition: 'width 0.5s' }} />
      </div>
      
      {/* Two columns */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Personal */}
        <div style={{ flex: 1, background: `${GOLD}10`, borderRadius: 10, padding: 12, border: `1px solid ${GOLD}30` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: GOLD }} />
            <Mo style={{ fontSize: 9, color: 'var(--gold)', letterSpacing: 1 }}>PERSONAL</Mo>
          </div>
          <Dp style={{ fontSize: 18, fontWeight: 700, color: 'var(--light)' }}>${data.personal_spending?.toLocaleString()}</Dp>
          <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 4 }}>{personalPct.toFixed(0)}% of total</Mo>
          {data.personal_fixed > 0 && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--faint)' }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)' }}>Fixed: ${data.personal_fixed?.toLocaleString()}</Mo>
              <Mo style={{ fontSize: 9, color: EMBER, display: 'block' }}>Discretionary: ${data.personal_discretionary?.toLocaleString()}</Mo>
            </div>
          )}
        </div>
        
        {/* Business */}
        <div style={{ flex: 1, background: '#22c55e10', borderRadius: 10, padding: 12, border: '1px solid #22c55e30' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
            <Mo style={{ fontSize: 9, color: '#22c55e', letterSpacing: 1 }}>BUSINESS</Mo>
          </div>
          <Dp style={{ fontSize: 18, fontWeight: 700, color: 'var(--light)' }}>${data.business_spending?.toLocaleString()}</Dp>
          <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 4 }}>{businessPct.toFixed(0)}% of total</Mo>
          {data.business_fixed > 0 && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--faint)' }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)' }}>Fixed costs: ${data.business_fixed?.toLocaleString()}</Mo>
            </div>
          )}
        </div>
      </div>
      
      <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 12, textAlign: 'center', fontStyle: 'italic' }}>
        Grade reflects personal discretionary spending only
      </Mo>
    </Card>
  );
}

/* ── Income Breakdown Card ── */
function IncomeBreakdown({ data }) {
  if (!data.has_profile || (!data.business_revenue && !data.salary_income)) return null;
  
  const isBusinessOwner = data.business_revenue > 0;
  
  return (
    <Card testId="income-breakdown">
      <SL>INCOME SOURCES</SL>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        {data.salary_income > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--surface)', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>💼</span>
              <Bd style={{ fontSize: 13, color: 'var(--light)' }}>Salary / Wages</Bd>
            </div>
            <Mo style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>+${data.salary_income?.toLocaleString()}</Mo>
          </div>
        )}
        {data.business_revenue > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--surface)', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>🏢</span>
              <Bd style={{ fontSize: 13, color: 'var(--light)' }}>Business Revenue</Bd>
            </div>
            <Mo style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>+${data.business_revenue?.toLocaleString()}</Mo>
          </div>
        )}
        {data.other_income > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--surface)', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>💰</span>
              <Bd style={{ fontSize: 13, color: 'var(--light)' }}>Other Income</Bd>
            </div>
            <Mo style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>+${data.other_income?.toLocaleString()}</Mo>
          </div>
        )}
      </div>
      {isBusinessOwner && data.personal_income > 0 && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: `${GOLD}10`, borderRadius: 8, border: `1px solid ${GOLD}30` }}>
          <Mo style={{ fontSize: 9, color: 'var(--gold)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>PERSONAL TAKE-HOME</Mo>
          <Dp style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)' }}>${data.personal_income?.toLocaleString()}</Dp>
          <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>After business revenue separation</Mo>
        </div>
      )}
    </Card>
  );
}

/* ── Investment Tracking Card ── */
function InvestmentCard({ data }) {
  if (!data.investment_spending || data.investment_spending <= 0) return null;
  
  return (
    <Card testId="investment-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 24 }}>📈</span>
        <div style={{ flex: 1 }}>
          <SL style={{ marginBottom: 4 }}>INVESTMENTS</SL>
          <Dp style={{ fontSize: 18, fontWeight: 700, color: DEBIT_RED }}>${data.investment_spending?.toLocaleString()}</Dp>
          <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>Not counted as discretionary spending</Mo>
        </div>
      </div>
    </Card>
  );
}

/* ── Tappable Category Row with Transaction Reclassification ── */
function CategoryRow({ cat, isExpanded, onToggle, comparison, onReclassifyTransaction, classifications, onReclassifyDual }) {
  const [editingTxn, setEditingTxn] = useState(null);
  const [showReclassifyModal, setShowReclassifyModal] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const icon = CAT_ICONS[cat.name] || 'O';
  const trendColor = TREND_COLORS[cat.trend_direction] || 'var(--mute)';
  const trendArrow = TREND_ARROWS[cat.trend_direction] || '';
  const isDisc = cat.classification === 'discretionary';
  const comp = comparison?.category_changes?.find(c => c.name === cat.name);

  const handleTxnClick = (txn) => {
    setSelectedTxn(txn);
    setShowReclassifyModal(true);
  };

  const handleTxnReclassify = (txn, classification) => {
    if (onReclassifyTransaction) {
      onReclassifyTransaction(txn.merchant, classification, 'transaction', txn);
    }
    setEditingTxn(null);
  };

  return (
    <div data-testid={`category-row-${cat.name}`} style={{ marginBottom: 4 }}>
      <button onClick={onToggle} style={{
        width: '100%', padding: '14px 0', background: 'none', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--faint)', textAlign: 'left',
      }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div className="dettio-flex dettio-flex--between dettio-flex--center">
            <Bd style={{ fontSize: 13, color: 'var(--light)', fontWeight: 600 }}>
              {cat.display_name || CATEGORY_LABELS[cat.name] || cat.name}
            </Bd>
            <div className="dettio-flex dettio-flex--center dettio-flex--gap-sm">
              {cat.trend_direction !== 'flat' && (
                <Mo style={{ fontSize: 10, color: trendColor, fontWeight: 700 }}>{trendArrow} {cat.trend_percent}%</Mo>
              )}
              <Mo style={{ fontSize: 13, fontWeight: 700, color: 'var(--light)' }}>${cat.amount?.toLocaleString()}</Mo>
            </div>
          </div>
          <div className="dettio-flex dettio-flex--center dettio-flex--gap-sm dettio-flex--wrap" style={{ marginTop: 2 }}>
            <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>{cat.pct}%</Mo>
            <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>${cat.monthly_avg?.toLocaleString()}/mo</Mo>
            {isDisc && <Mo style={{ fontSize: 8, color: EMBER, fontWeight: 700, background: `${EMBER}15`, padding: '1px 6px', borderRadius: 4 }}>OPPORTUNITY</Mo>}
            {comp && comp.delta_pct !== 0 && (
              <Mo data-testid={`comparison-badge-${cat.name}`} style={{
                fontSize: 8, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                color: comp.direction === 'down' ? '#22c55e' : '#ef4444',
                background: comp.direction === 'down' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
              }}>
                {comp.direction === 'down' ? '\u2193' : '\u2191'} {Math.abs(comp.delta_pct)}% vs last
              </Mo>
            )}
          </div>
        </div>
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="2"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {isExpanded && (
        <div data-testid={`category-detail-${cat.name}`} className="fade-in" style={{ padding: '12px 0 12px 28px' }}>
          {/* Transactions list with reclassification */}
          {cat.transactions?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 8 }}>
                TRANSACTIONS ({cat.transactions.length})
              </Mo>
              {cat.transactions.slice(0, 10).map((txn, i) => {
                const txnKey = `${txn.merchant}-${txn.date}-${txn.amount}`;
                const merchantClassData = classifications?.[txn.merchant];
                const merchantClassLabel = getClassificationLabel(merchantClassData);
                
                return (
                  <div key={i} style={{ 
                    position: 'relative', 
                    padding: '8px 0', 
                    borderBottom: i < Math.min(cat.transactions.length, 10) - 1 ? '1px solid var(--faint)' : 'none',
                  }}>
                    <button
                      data-testid={`transaction-row-${i}`}
                      onClick={() => handleTxnClick(txn)}
                      style={{
                        width: '100%',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div className="dettio-flex dettio-flex--between dettio-flex--center">
                        <div style={{ flex: 1 }}>
                          <Bd style={{ fontSize: 12, color: 'var(--light)' }}>{txn.merchant}</Bd>
                          <Mo style={{ fontSize: 9, color: 'var(--mute)' }}>{fmtDate(txn.date)}</Mo>
                        </div>
                        <Mo style={{ fontSize: 12, fontWeight: 600, color: 'var(--light)', marginRight: 8 }}>${txn.amount?.toLocaleString()}</Mo>
                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={merchantClassLabel ? GOLD : 'var(--mute)'} strokeWidth="2" strokeLinecap="round">
                          <path d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                    {merchantClassLabel && (
                      <Mo style={{ fontSize: 8, color: 'var(--gold)', marginTop: 2 }}>
                        Merchant rule: {merchantClassLabel}
                      </Mo>
                    )}
                  </div>
                );
              })}
              {cat.transactions.length > 10 && (
                <Mo style={{ fontSize: 10, color: 'var(--mute)', marginTop: 8, textAlign: 'center' }}>
                  +{cat.transactions.length - 10} more transactions
                </Mo>
              )}
            </div>
          )}
          
          {/* Top vendors summary */}
          {cat.top_vendors?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 6 }}>TOP MERCHANTS</Mo>
              {cat.top_vendors.slice(0, 3).map((v, i) => (
                <div key={i} className="dettio-flex dettio-flex--between" style={{ padding: '6px 0', borderBottom: i < 2 ? '1px solid var(--faint)' : 'none' }}>
                  <div>
                    <Bd style={{ fontSize: 12, color: 'var(--light)' }}>{v.name}</Bd>
                    <Mo style={{ fontSize: 9, color: 'var(--mute)' }}>{v.count} txns · {v.pct_of_category}%</Mo>
                  </div>
                  <Mo style={{ fontSize: 12, fontWeight: 700, color: 'var(--light)' }}>${v.total?.toLocaleString()}</Mo>
                </div>
              ))}
            </div>
          )}
          {cat.monthly_data && <MiniBarChart data={cat.monthly_data} />}
          {cat.transactions?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 6 }}>RECENT TRANSACTIONS</Mo>
              {cat.transactions.slice(0, 5).map((t, i) => (
                <div key={i} className="dettio-flex dettio-flex--between" style={{ padding: '4px 0' }}>
                  <Mo style={{ fontSize: 11, color: 'var(--mute)' }}>{t.date} · {t.merchant}</Mo>
                  <Mo style={{ fontSize: 11, color: 'var(--light)' }}>${t.amount?.toLocaleString()}</Mo>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Reclassify Modal */}
      {showReclassifyModal && selectedTxn && (
        <ReclassifyTransactionModal
          transaction={selectedTxn}
          currentType={cat.account_type || 'personal'}
          currentCategory={cat.name}
          onSave={async (data) => {
            if (onReclassifyDual) {
              await onReclassifyDual(data);
            }
            setShowReclassifyModal(false);
            setSelectedTxn(null);
          }}
          onClose={() => {
            setShowReclassifyModal(false);
            setSelectedTxn(null);
          }}
        />
      )}
    </div>
  );
}

/* ── Scenario Slider ── */
function ScenarioSlider({ category, value, onChange }) {
  const savings = Math.round(category.monthly_avg * (value / 100));
  return (
    <div data-testid={`slider-${category.name}`} style={{ marginBottom: 16, padding: '12px', background: 'var(--faint)', borderRadius: 10 }}>
      <div className="dettio-flex dettio-flex--between" style={{ marginBottom: 6 }}>
        <Bd style={{ fontSize: 13, color: 'var(--light)', fontWeight: 600 }}>{category.display_name || CATEGORY_LABELS[category.name]}</Bd>
        <Mo style={{ fontSize: 12, fontWeight: 700, color: savings > 0 ? '#22c55e' : 'var(--mute)' }}>
          {savings > 0 ? `+$${savings}/mo` : '$0'}
        </Mo>
      </div>
      <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginBottom: 8 }}>
        Currently ${category.monthly_avg?.toLocaleString()}/mo · Cut by {value}%
      </Mo>
      <input type="range" min="0" max="100" step="5" value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        style={{ width: '100%', accentColor: GOLD, height: 6, cursor: 'pointer' }} />
    </div>
  );
}

/* ── Phased Reveal ── */
function Phase({ delay, children, visible }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [delay, visible]);
  if (!show) return null;
  return <div className="fade-in">{children}</div>;
}

/* ── AI Written Insights Card ── */
const INSIGHT_ICONS = {
  warning: { icon: '⚠️', color: EMBER, bg: `${EMBER}10`, border: `${EMBER}30` },
  opportunity: { icon: '💡', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)' },
  positive: { icon: '✨', color: '#22c55e', bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)' },
  tip: { icon: '💰', color: GOLD, bg: `${GOLD}10`, border: `${GOLD}30` },
};

function AIInsightsCard({ insights, loading, error }) {
  if (loading) {
    return (
      <Card testId="ai-insights-loading" style={{ background: `${GOLD}06`, border: `1px solid ${GOLD}20` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ 
            width: 28, height: 28, borderRadius: '50%', 
            background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 14 }}>✨</span>
          </div>
          <SL style={{ margin: 0 }}>AI INSIGHTS</SL>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0' }}>
          <div style={{ 
            width: 20, height: 20, border: `2px solid ${GOLD}`, 
            borderTopColor: 'transparent', borderRadius: '50%', 
            animation: 'spin 1s linear infinite' 
          }} />
          <Mo style={{ fontSize: 12, color: 'var(--mute)' }}>Analyzing your spending patterns...</Mo>
        </div>
      </Card>
    );
  }

  if (error || !insights || insights.length === 0) {
    return null; // Don't show card if no insights
  }

  return (
    <Card testId="ai-insights-card" style={{ background: `${GOLD}06`, border: `1px solid ${GOLD}20` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ 
          width: 28, height: 28, borderRadius: '50%', 
          background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 14 }}>✨</span>
        </div>
        <SL style={{ margin: 0 }}>AI INSIGHTS</SL>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {insights.map((insight, i) => {
          const style = INSIGHT_ICONS[insight.type] || INSIGHT_ICONS.tip;
          return (
            <div 
              key={i} 
              data-testid={`ai-insight-${i}`}
              style={{ 
                padding: '12px 14px', 
                background: style.bg, 
                border: `1px solid ${style.border}`,
                borderRadius: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{style.icon}</span>
                <div style={{ flex: 1 }}>
                  <Bd style={{ fontSize: 12, fontWeight: 700, color: style.color, display: 'block', marginBottom: 4 }}>
                    {insight.title}
                  </Bd>
                  <Bd style={{ fontSize: 12, color: 'var(--light)', display: 'block', lineHeight: 1.6 }}>
                    {insight.body}
                  </Bd>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', textAlign: 'center', marginTop: 12, fontStyle: 'italic' }}>
        Powered by AI • Insights based on your financial profile
      </Mo>
    </Card>
  );
}

/* ══════════════════════════════════════════════
   REVEAL SCREEN — Phase 2 + 3 Enhanced
   ══════════════════════════════════════════════ */
export function RevealScreen({ data, aiData, onBack, debts, onSetBudgets, onReAnalyze }) {
  const [expandedCat, setExpandedCat] = useState(null);
  const [sliderValues, setSliderValues] = useState({});
  const [ready, setReady] = useState(false);
  const [classifications, setClassifications] = useState({});
  const classificationsRef = useRef({});
  const [hasChanges, setHasChanges] = useState(false);
  const [reAnalyzing, setReAnalyzing] = useState(false);
  
  // Confirmation dialog state for bulk merchant reclassification
  const [confirmDialog, setConfirmDialog] = useState(null); // { merchant, optionId, txnCount }
  
  // AI Written Insights state
  const [aiInsights, setAiInsights] = useState(null);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(true);
  const [aiInsightsError, setAiInsightsError] = useState(null);
  
  // Dismissed subscriptions state
  const [dismissedSubs, setDismissedSubs] = useState(new Set());
  const [dismissing, setDismissing] = useState(null);
  
  // Quick Fix modal state
  const [showQuickFix, setShowQuickFix] = useState(false);

  // Local mutable copy of categories for optimistic UI updates
  const [localCategories, setLocalCategories] = useState(data.categories || []);

  // Sync localCategories when data prop changes (e.g. after re-analysis)
  useEffect(() => {
    setLocalCategories(data.categories || []);
  }, [data.categories]);
  
  useEffect(() => { setReady(true); }, []);
  
  // Fetch AI Written Insights (cached or generate new)
  useEffect(() => {
    const fetchAiInsights = async () => {
      if (!data?.id) return;
      
      // Check if insights are already in the data (cached)
      if (data.ai_written_insights && data.ai_written_insights.length > 0) {
        setAiInsights(data.ai_written_insights);
        setAiInsightsLoading(false);
        return;
      }
      
      setAiInsightsLoading(true);
      setAiInsightsError(null);
      
      try {
        const res = await fetch(`${API}/api/insights/generate-ai-insights`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ analysis_id: data.id }),
        });
        
        if (res.ok) {
          const result = await res.json();
          setAiInsights(result.insights || []);
        } else {
          setAiInsightsError('Failed to generate insights');
        }
      } catch (err) {
        console.error('Failed to fetch AI insights:', err);
        setAiInsightsError(err.message);
      } finally {
        setAiInsightsLoading(false);
      }
    };
    
    fetchAiInsights();
  }, [data?.id, data?.ai_written_insights]);
  
  // Load existing classifications
  useEffect(() => {
    const loadClassifications = async () => {
      try {
        const res = await fetch(`${API}/api/insights/merchant-classifications`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const loaded = data.classifications || {};
          setClassifications(loaded);
          classificationsRef.current = loaded;
        }
      } catch {}
    };
    loadClassifications();
  }, []);

  // Handle reclassification - shows confirmation for merchant-level rule
  const handleReclassify = async (merchant, optionId, type, txn = null) => {
    // Count how many transactions from this merchant exist in the data
    let txnCount = 0;
    let sampleTxn = txn;  // Keep reference to transaction for direction detection
    
    if (localCategories?.length > 0) {
      for (const cat of localCategories) {
        if (cat.transactions) {
          const merchantTxns = cat.transactions.filter(t => t.merchant === merchant);
          txnCount += merchantTxns.length;
          if (!sampleTxn && merchantTxns.length > 0) {
            sampleTxn = merchantTxns[0];  // Get sample transaction if not provided
          }
        }
        if (cat.top_vendors) {
          const vendor = cat.top_vendors.find(v => v.name === merchant);
          if (vendor) txnCount = Math.max(txnCount, vendor.count || 0);
        }
      }
    }
    
    // If more than 1 transaction from this merchant, ask for confirmation
    if (txnCount > 1) {
      setConfirmDialog({ merchant, optionId, txnCount, txn: sampleTxn });
      return;
    }
    
    // Single transaction or couldn't count - apply directly as merchant rule
    await applyMerchantRule(merchant, optionId, sampleTxn);
  };
  
  // Apply merchant-level rule (saves to backend)
  const applyMerchantRule = async (merchant, optionId, txn = null) => {
    const classData = buildClassificationData(optionId);
    
    // Determine direction from transaction if available
    let direction = 'debit';  // Default to debit (spending)
    if (txn && txn.amount !== undefined) {
      direction = txn.amount >= 0 ? 'debit' : 'credit';
    }
    
    // Add direction to classification data
    classData.direction = direction;
    
    // Build composite key
    const compositeKey = `${merchant}__${direction}`;
    
    const newClassifications = {
      ...classificationsRef.current,
      [compositeKey]: classData,  // Use composite key instead of merchant name only
    };
    classificationsRef.current = newClassifications;
    setClassifications(newClassifications);
    setHasChanges(true);
    setConfirmDialog(null);
    
    // Save to backend
    try {
      await fetch(`${API}/api/insights/merchant-classifications`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ classifications: newClassifications }),
      });
    } catch (err) {
      console.error('Failed to save classification:', err);
    }
  };

  // Apply classification to just this one transaction (no merchant rule)
  const applyIndividualClassification = async (merchant, optionId, txnId) => {
    const classData = buildClassificationData(optionId);
    
    // Apply as temporary classification without saving merchant rule
    const newClassifications = {
      ...classificationsRef.current,
      [merchant]: classData,
    };
    classificationsRef.current = newClassifications;
    setClassifications(newClassifications);
    setHasChanges(true);
    setConfirmDialog(null);
    
    // Note: This will be handled by individual transaction reclassification in the backend
    // The key difference is we don't save to merchant_classifications collection
  };

  // Handle re-analysis
  const handleReAnalyze = async () => {
    if (!onReAnalyze) return;
    setReAnalyzing(true);
    // Reset AI insights to trigger regeneration after re-analysis
    setAiInsights(null);
    setAiInsightsLoading(true);
    try {
      await onReAnalyze();
      setHasChanges(false);
      // AI insights will be regenerated when new data.id changes
    } catch (err) {
      console.error('Re-analysis failed:', err);
    } finally {
      setReAnalyzing(false);
    }
  };

  // Handle dual reclassification (both account type AND category)
  const handleReclassifyDual = async (reclassData) => {
    const { merchant, accountType, category, applyToAll, transactionId } = reclassData;
    
    // Find the transaction to get its amount/direction
    let txnAmount = 0;
    if (localCategories?.length > 0) {
      for (const cat of localCategories) {
        if (cat.transactions) {
          const txn = cat.transactions.find(t => 
            t.merchant === merchant || 
            `${t.merchant}-${t.date}-${t.amount}` === transactionId
          );
          if (txn) {
            txnAmount = txn.amount;
            break;
          }
        }
      }
    }
    
    // Determine direction: "debit" for spending (amount >= 0), "credit" for income (amount < 0)
    const direction = txnAmount >= 0 ? 'debit' : 'credit';
    const compositeKey = `${merchant}__${direction}`;
    
    // Find the category that this transaction belongs to in order to determine current account type
    const catObj = ALL_CATEGORIES.find(c => c.value === category);
    
    // Build classification data
    const classData = {
      type: accountType,
      classification: catObj?.classification || (accountType === 'business' ? 'business_operations' : 'personal'),
      category_override: category,
      direction: direction,
      reclassified_at: new Date().toISOString(),
    };
    
    if (applyToAll) {
      // Apply as merchant rule (permanent) using composite key
      const newClassifications = {
        ...classificationsRef.current,
        [compositeKey]: classData,
      };
      classificationsRef.current = newClassifications;
      setClassifications(newClassifications);
      setHasChanges(true);
      
      // Save to backend
      try {
        await fetch(`${API}/api/insights/merchant-classifications`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ classifications: newClassifications }),
        });
      } catch (err) {
        console.error('Failed to save merchant rule:', err);
      }
    } else {
      // Single transaction only - just mark as having changes
      setHasChanges(true);
    }

    // ── Optimistic UI update: remove transaction(s) from their current category ──
    setLocalCategories(prev =>
      prev
        .map(cat => ({
          ...cat,
          transactions: (cat.transactions || []).filter(txn => {
              if (applyToAll) {
                return txn.merchant !== merchant;
              }
              // Single transaction: remove if Plaid ID matches OR composite key matches
              const matchById = txn.id && txn.id === transactionId;
              const matchByKey = `${txn.merchant}-${txn.date}-${txn.amount}` === transactionId;
              return !matchById && !matchByKey;
            }),
        }))
        .filter(cat => (cat.transactions?.length ?? 0) > 0)
    );
  };

  // Handle dismiss subscription
  const handleDismissSubscription = async (merchantName) => {
    setDismissing(merchantName);
    try {
      await fetch(`${API}/api/insights/dismiss-subscription`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ merchant_name: merchantName }),
      });
      setDismissedSubs(prev => new Set([...prev, merchantName]));
    } catch (err) {
      console.error('Failed to dismiss subscription:', err);
    } finally {
      setDismissing(null);
    }
  };


  const grade = aiData?.spending_grade || (data.income_pct < 50 ? 'A' : data.income_pct < 70 ? 'B' : data.income_pct < 85 ? 'C' : data.income_pct < 100 ? 'D' : 'F');
  const gradeColor = GRADE_COLORS[grade] || GRADE_COLORS.C;
  const discCats = (localCategories || []).filter(c => c.classification === 'discretionary');
  const comparison = data.comparison;

  const totalSave = discCats.reduce((s, c) => s + Math.round(c.monthly_avg * ((sliderValues[c.name] || 0) / 100)), 0);
  const totalDebt = debts?.reduce((s, d) => s + (d.balance || 0), 0) || 0;
  const avgApr = debts?.length ? debts.reduce((s, d) => s + (d.apr || 0), 0) / debts.length : 0;
  const curPay = debts?.reduce((s, d) => s + (d.min_payment || 0) + (d.extra || 0), 0) || 0;
  const calcM = (p) => { if (p <= 0 || totalDebt <= 0) return 999; const r = avgApr / 100 / 12; if (r === 0) return Math.ceil(totalDebt / p); const m = -Math.log(1 - (r * totalDebt / p)) / Math.log(1 + r); return Math.ceil(Math.max(m, 0)) || 999; };
  const curM = calcM(curPay);
  const newM = calcM(curPay + totalSave);
  const monthsSaved = Math.max(curM - newM, 0);

  return (
    <div data-testid="insights-reveal" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div style={{ height: 'env(safe-area-inset-top, 20px)', minHeight: 20 }} />
        <BackBtn onClick={onBack} label="BACK TO INSIGHTS" />

        {/* AI Written Insights Card - First thing users see */}
        <Phase delay={0} visible={ready}>
          <AIInsightsCard 
            insights={aiInsights} 
            loading={aiInsightsLoading} 
            error={aiInsightsError} 
          />
        </Phase>

        {/* Quick Fix Button */}
        <Phase delay={100} visible={ready}>
          <button
            data-testid="quick-fix-btn"
            onClick={() => setShowQuickFix(true)}
            style={{
              width: '100%',
              padding: '16px 20px',
              background: `linear-gradient(135deg, ${GOLD}15, ${GOLD}08)`,
              border: `1px solid ${GOLD}40`,
              borderRadius: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: `${GOLD}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <div style={{ textAlign: 'left' }}>
                <Bd style={{ fontSize: 14, fontWeight: 700, color: 'var(--light)', display: 'block' }}>Quick Fix</Bd>
                <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 2 }}>Swipe to categorize transactions fast</Mo>
              </div>
            </div>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </Phase>

        {/* Comparison banner */}
        {comparison && (
          <Phase delay={200} visible={ready}>
            <Card testId="comparison-banner" style={{
              background: comparison.spending_delta < 0 ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)',
              border: comparison.spending_delta < 0 ? '1px solid rgba(34,197,94,0.2)' : '1px solid rgba(239,68,68,0.2)',
            }}>
              <Mo style={{ fontSize: 10, letterSpacing: 1.5, display: 'block', marginBottom: 6, color: comparison.spending_delta < 0 ? '#22c55e' : DEBIT_RED }}>
                VS LAST ANALYSIS ({fmtDate(comparison.prev_analysis_date)})
              </Mo>
              <div className="dettio-flex dettio-flex--center dettio-flex--gap-lg">
                <Dp style={{ fontSize: 20, fontWeight: 800, color: comparison.spending_delta < 0 ? '#22c55e' : DEBIT_RED }}>
                  {comparison.spending_delta < 0 ? '\u2193' : '\u2191'} ${Math.abs(comparison.spending_delta).toLocaleString()}
                </Dp>
                <Bd style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.5 }}>
                  {comparison.spending_delta < 0
                    ? `You spent ${Math.abs(comparison.spending_delta_pct)}% less than your last analysis. Keep it up!`
                    : `Spending is up ${comparison.spending_delta_pct}% since your last analysis.`}
                </Bd>
              </div>
            </Card>
          </Phase>
        )}

        {/* Grade + Opening */}
        <Phase delay={comparison ? 300 : 0} visible={ready}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div data-testid="spending-grade" style={{ width: 80, height: 80, borderRadius: '50%', border: `3px solid ${gradeColor}`, margin: '0 auto 16px' }} className="dettio-avatar">
              <Dp style={{ fontSize: 36, fontWeight: 800, color: gradeColor }}>{grade}</Dp>
            </div>
            {aiData?.headline && <Bd style={{ fontSize: 15, color: 'var(--light)', display: 'block', lineHeight: 1.5 }}>{aiData.headline}</Bd>}
          </div>
          {aiData?.opening_narrative && (
            <Card testId="opening-narrative" style={{ background: `${GOLD}06`, border: `1px solid ${GOLD}20` }}>
              <Bd style={{ fontSize: 14, color: 'var(--light)', display: 'block', lineHeight: 1.7, fontStyle: 'italic' }}>
                "{aiData.opening_narrative}"
              </Bd>
            </Card>
          )}
        </Phase>

        {/* Bad News */}
        <Phase delay={comparison ? 600 : 400} visible={ready}>
          {aiData?.bad_news && (
            <Card testId="bad-news-card" style={{ background: `${EMBER}08`, border: `1px solid ${EMBER}25` }}>
              <Mo style={{ fontSize: 10, color: EMBER, letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>{(aiData.bad_news.title || 'THE HARD TRUTH').toUpperCase()}</Mo>
              <Dp style={{ fontSize: 22, fontWeight: 800, color: EMBER, display: 'block', marginBottom: 6 }}>{aiData.bad_news.stat}</Dp>
              <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.6 }}>{aiData.bad_news.body}</Bd>
            </Card>
          )}
        </Phase>

        {/* Spending vs Income */}
        <Phase delay={comparison ? 800 : 700} visible={ready}>
          <Card testId="spending-income-card">
            <SL>{data.has_business_tracking ? 'PERSONAL SPENDING VS INCOME' : 'SPENDING VS INCOME'}</SL>
            <div className="dettio-flex dettio-flex--between" style={{ marginBottom: 8 }}>
              <div>
                <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block' }}>SPENT</Mo>
                <Dp style={{ fontSize: 20, fontWeight: 800, color: EMBER }}>
                  ${data.has_business_tracking ? data.personal_spending?.toLocaleString() : data.total_spending?.toLocaleString()}
                </Dp>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block' }}>INCOME</Mo>
                <Dp style={{ fontSize: 20, fontWeight: 800, color: '#22c55e' }}>
                  ${data.has_business_tracking ? data.personal_income?.toLocaleString() : data.total_income?.toLocaleString()}
                </Dp>
              </div>
            </div>
            <div style={{ height: 8, background: 'var(--faint)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(data.income_pct, 100)}%`, background: data.income_pct > 85 ? EMBER : GOLD, borderRadius: 4, transition: 'width 1s ease' }} />
            </div>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', textAlign: 'center', marginTop: 6 }}>{data.income_pct}% of income spent</Mo>
          </Card>
        </Phase>

        {/* Liquid Fill Gauge - Debt-to-Income Ratio */}
        <Phase delay={comparison ? 875 : 825} visible={ready}>
          {data.income_pct > 0 && (
            <Card testId="dti-liquid-gauge" style={{ textAlign: 'center', padding: 24 }}>
              <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 16 }}>
                DEBT-TO-INCOME RATIO
              </Mo>
              <LiquidFillGauge 
                percentage={data.income_pct} 
                label="DTI"
                size={160}
                liquidColor={data.income_pct > 85 ? '#ef4444' : data.income_pct > 50 ? '#eab308' : '#00FFCC'}
              />
              <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 16, lineHeight: 1.6 }}>
                {data.income_pct <= 35 && '✨ Excellent financial health'}
                {data.income_pct > 35 && data.income_pct <= 50 && '👍 Good spending control'}
                {data.income_pct > 50 && data.income_pct <= 85 && '⚠️ Consider reducing spending'}
                {data.income_pct > 85 && '🚨 High debt burden - take action'}
              </Mo>
            </Card>
          )}
        </Phase>

        {/* Personal vs Business Split (if applicable) */}
        <Phase delay={comparison ? 900 : 850} visible={ready}>
          <PersonalBusinessSplit data={data} />
        </Phase>

        {/* Income Breakdown (if profile exists) */}
        <Phase delay={comparison ? 950 : 900} visible={ready}>
          <IncomeBreakdown data={data} />
        </Phase>

        {/* Investment Card (if applicable) */}
        <Phase delay={comparison ? 980 : 930} visible={ready}>
          <InvestmentCard data={data} />
        </Phase>

        {/* Category Breakdown */}
        <Phase delay={comparison ? 1000 : 1000} visible={ready}>
          <Card testId="category-breakdown">
            <SL>{data.has_business_tracking ? 'WHERE YOUR PERSONAL MONEY GOES' : 'WHERE YOUR MONEY GOES'}</SL>
            <CategoryDonutChart categories={localCategories} />
            {localCategories?.map((c) => (
              <CategoryRow key={c.name} cat={c} comparison={comparison}
                isExpanded={expandedCat === c.name}
                onToggle={() => setExpandedCat(expandedCat === c.name ? null : c.name)}
                onReclassifyTransaction={handleReclassify}
                onReclassifyDual={handleReclassifyDual}
                classifications={classifications}
              />
            ))}
          </Card>
        </Phase>

        {/* Good News */}
        <Phase delay={1300} visible={ready}>
          {aiData?.good_news && (
            <Card testId="good-news-card" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <Mo style={{ fontSize: 10, color: '#22c55e', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>{(aiData.good_news.title || 'THE SILVER LINING').toUpperCase()}</Mo>
              <Bd style={{ fontSize: 13, color: 'var(--light)', display: 'block', lineHeight: 1.6 }}>{aiData.good_news.body}</Bd>
            </Card>
          )}
        </Phase>

        {/* Top 3 Actions */}
        <Phase delay={1600} visible={ready}>
          {aiData?.top_3_actions?.length > 0 && (
            <Card testId="top-actions">
              <SL>TOP 3 ACTIONS</SL>
              {aiData.top_3_actions.map((a, i) => (
                <div key={i} style={{ padding: '12px 0', borderBottom: i < 2 ? '1px solid var(--faint)' : 'none' }}>
                  <div className="dettio-flex dettio-flex--center dettio-flex--gap-sm" style={{ marginBottom: 4 }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: `${GOLD}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Mo style={{ fontSize: 11, fontWeight: 800, color: 'var(--gold)' }}>{i + 1}</Mo>
                    </div>
                    <Bd style={{ fontSize: 14, fontWeight: 700, color: 'var(--light)' }}>{a.title}</Bd>
                  </div>
                  <Bd style={{ fontSize: 12, color: 'var(--mute)', display: 'block', lineHeight: 1.6, paddingLeft: 34 }}>{a.body}</Bd>
                  {a.monthly_savings > 0 && (
                    <Mo style={{ fontSize: 11, color: '#22c55e', fontWeight: 700, display: 'block', paddingLeft: 34, marginTop: 4 }}>
                      Save ${a.monthly_savings}/mo · {a.impact}
                    </Mo>
                  )}
                </div>
              ))}
            </Card>
          )}
        </Phase>

        {/* Business Category Breakdown (if applicable) */}
        {data.has_business_tracking && data.business_categories?.length > 0 && (
          <Phase delay={1950} visible={ready}>
            <Card testId="business-category-breakdown" style={{ borderLeft: '3px solid #22c55e' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 20 }}>💼</span>
                <SL style={{ margin: 0 }}>BUSINESS EXPENSES</SL>
              </div>
              <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginBottom: 16 }}>
                These costs are excluded from your spending grade. Tap edit to reclassify if needed.
              </Mo>
              {data.business_merchants?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 8 }}>TOP BUSINESS MERCHANTS</Mo>
                  <TopMerchantsChart 
                    merchants={data.business_merchants} 
                    onReclassify={handleReclassify}
                    classifications={classifications}
                    isBusiness={true}
                  />
                </div>
              )}
              {data.business_categories.slice(0, 6).map((c, i) => (
                <div key={i} className="dettio-flex dettio-flex--between" style={{ padding: '10px 0', borderBottom: i < Math.min(data.business_categories.length, 6) - 1 ? '1px solid var(--faint)' : 'none' }}>
                  <Bd style={{ fontSize: 13, color: 'var(--light)' }}>{c.display_name || c.name}</Bd>
                  <Mo style={{ fontSize: 13, fontWeight: 700, color: 'var(--light)' }}>${c.amount?.toLocaleString()}</Mo>
                </div>
              ))}
              <div style={{ marginTop: 12, padding: '12px', background: '#22c55e10', borderRadius: 8, border: '1px solid #22c55e30' }}>
                <div className="dettio-flex dettio-flex--between">
                  <Mo style={{ fontSize: 10, color: DEBIT_RED, letterSpacing: 1 }}>TOTAL BUSINESS</Mo>
                  <Dp style={{ fontSize: 18, fontWeight: 700, color: DEBIT_RED }}>${data.business_spending?.toLocaleString()}</Dp>
                </div>
              </div>
            </Card>
          </Phase>
        )}

        {/* Subscriptions */}
        <Phase delay={2100} visible={ready}>
          {data.subscriptions?.length > 0 && (
            <Card testId="subscriptions-card">
              <SL>DETECTED SUBSCRIPTIONS</SL>
              {data.subscriptions.filter(s => !dismissedSubs.has(s.name)).map((s, i, arr) => (
                <div key={i} className="dettio-flex dettio-flex--between dettio-flex--center" style={{ padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--faint)' : 'none' }}>
                  <div style={{ flex: 1 }}>
                    <Bd style={{ fontSize: 13, color: 'var(--light)' }}>{s.name}</Bd>
                    <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{s.frequency} · {s.months_seen} months</Mo>
                  </div>
                  <Mo style={{ fontSize: 13, fontWeight: 700, color: 'var(--light)', marginRight: 12 }}>${s.amount}/mo</Mo>
                  <button
                    onClick={() => handleDismissSubscription(s.name)}
                    disabled={dismissing === s.name}
                    data-testid={`dismiss-subscription-${i}`}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: dismissing === s.name ? 'wait' : 'pointer',
                      padding: 4,
                      opacity: dismissing === s.name ? 0.5 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Dismiss - not a subscription"
                  >
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="2" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
              {data.subscriptions.filter(s => !dismissedSubs.has(s.name)).length > 0 && (
                <div className="dettio-stat-box" style={{ marginTop: 12 }}>
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1.5, display: 'block' }}>TOTAL SUBSCRIPTIONS</Mo>
                  <Dp style={{ fontSize: 20, fontWeight: 800, color: EMBER }}>
                    ${data.subscriptions.filter(s => !dismissedSubs.has(s.name)).reduce((s, x) => s + x.amount, 0).toFixed(0)}/mo
                  </Dp>
                </div>
              )}
            </Card>
          )}
        </Phase>

        {/* Red Flags */}
        <Phase delay={2100} visible={ready}>
          {aiData?.red_flags?.length > 0 && (
            <Card testId="red-flags-card" style={{ background: `${EMBER}08`, border: `1px solid ${EMBER}30` }}>
              <Mo style={{ fontSize: 10, color: EMBER, letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>RED FLAGS</Mo>
              {aiData.red_flags.map((f, i) => (
                <div key={i} style={{ padding: '10px 0', borderBottom: i < aiData.red_flags.length - 1 ? `1px solid ${EMBER}20` : 'none' }}>
                  <div className="dettio-flex dettio-flex--between">
                    <Bd style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)' }}>{f.merchant}</Bd>
                    <Mo style={{ fontSize: 12, fontWeight: 700, color: EMBER }}>${f.amount?.toLocaleString()}</Mo>
                  </div>
                  <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 4 }}>{f.suggestion}</Bd>
                </div>
              ))}
            </Card>
          )}
        </Phase>

        {/* Scenario Sliders */}
        <Phase delay={2400} visible={ready}>
          {discCats.length > 0 && totalDebt > 0 && (
            <Card testId="scenario-sliders">
              <SL>WHAT IF YOU CUT BACK?</SL>
              <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 16, lineHeight: 1.6 }}>
                Drag the sliders to see how cutting spending could accelerate your debt payoff.
              </Bd>
              {discCats.map(cat => (
                <ScenarioSlider key={cat.name} category={cat}
                  value={sliderValues[cat.name] || 0}
                  onChange={(v) => setSliderValues(p => ({ ...p, [cat.name]: v }))} />
              ))}
              <div data-testid="scenario-summary" style={{ marginTop: 16, padding: '16px', background: totalSave > 0 ? 'rgba(34,197,94,0.08)' : 'var(--faint)', border: totalSave > 0 ? '1px solid rgba(34,197,94,0.25)' : '1px solid var(--faint)', borderRadius: 10, textAlign: 'center' }}>
                <div className="dettio-flex" style={{ justifyContent: 'space-around', marginBottom: 12 }}>
                  <div>
                    <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1.5, display: 'block' }}>MONTHLY SAVINGS</Mo>
                    <Dp style={{ fontSize: 22, fontWeight: 800, color: totalSave > 0 ? '#22c55e' : 'var(--mute)' }}>${totalSave.toLocaleString()}</Dp>
                  </div>
                  <div>
                    <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1.5, display: 'block' }}>MONTHS SAVED</Mo>
                    <Dp style={{ fontSize: 22, fontWeight: 800, color: monthsSaved > 0 ? '#22c55e' : 'var(--mute)' }}>
                      {monthsSaved > 0 && monthsSaved < 999 ? monthsSaved : '\u2014'}
                    </Dp>
                  </div>
                </div>
                {totalSave > 0 && monthsSaved > 0 && monthsSaved < 999 && (
                  <Bd style={{ fontSize: 12, color: 'var(--light)', display: 'block', lineHeight: 1.5 }}>
                    Redirecting <span style={{ color: '#22c55e', fontWeight: 700 }}>${totalSave}/mo</span> to debt
                    gets you debt-free <span style={{ color: '#22c55e', fontWeight: 700 }}>{monthsSaved} months sooner</span>
                  </Bd>
                )}
              </div>
            </Card>
          )}
        </Phase>

        {/* AI Scenarios */}
        <Phase delay={2600} visible={ready}>
          {aiData?.scenarios?.length > 0 && (
            <Card testId="ai-scenarios">
              <SL>AI-POWERED SCENARIOS</SL>
              {aiData.scenarios.map((s, i) => (
                <div key={i} style={{ padding: '12px', marginBottom: 8, background: 'var(--faint)', borderRadius: 8 }}>
                  <Bd style={{ fontSize: 13, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 4 }}>{s.name}</Bd>
                  <Bd style={{ fontSize: 12, color: 'var(--mute)', display: 'block', lineHeight: 1.5, marginBottom: 8 }}>{s.description}</Bd>
                  <div className="dettio-flex dettio-flex--gap-lg">
                    {s.monthly_savings > 0 && <Mo style={{ fontSize: 11, color: '#22c55e', fontWeight: 700 }}>+${s.monthly_savings}/mo</Mo>}
                    {s.months_saved > 0 && <Mo style={{ fontSize: 11, color: '#22c55e', fontWeight: 700 }}>{s.months_saved} mo sooner</Mo>}
                    {s.interest_saved > 0 && <Mo className="interest-saved-pulse" style={{ fontSize: 13, color: '#22c55e', fontWeight: 700 }}>${s.interest_saved} interest saved</Mo>}
                  </div>
                </div>
              ))}
            </Card>
          )}
        </Phase>

        {/* Coaching */}
        <Phase delay={2800} visible={ready}>
          {aiData?.coaching_message && (
            <Card testId="coaching-card" style={{ background: `${GOLD}08`, border: `1px solid ${GOLD}30` }}>
              <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>YOUR COACH SAYS</Mo>
              <Bd style={{ fontSize: 13, color: 'var(--light)', display: 'block', lineHeight: 1.6 }}>{aiData.coaching_message}</Bd>
              {aiData.monthly_savings_potential > 0 && (
                <div className="dettio-stat-box" style={{ marginTop: 12 }}>
                  <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1.5, display: 'block' }}>MONTHLY SAVINGS POTENTIAL</Mo>
                  <Dp style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>${aiData.monthly_savings_potential.toLocaleString()}</Dp>
                </div>
              )}
            </Card>
          )}
        </Phase>

        {/* Monthly Trend */}
        <Phase delay={3000} visible={ready}>
          {data.monthly_trend?.length > 1 && (
            <Card testId="monthly-trend">
              <SL>MONTH-OVER-MONTH SPENDING</SL>
              <MonthlyTrendChart data={data.monthly_trend} />
            </Card>
          )}
        </Phase>

        {/* Top Merchants */}
        <Phase delay={3200} visible={ready}>
          {data.top_merchants?.length > 0 && (
            <Card testId="top-merchants">
              <SL>TOP MERCHANTS BY SPEND</SL>
              <TopMerchantsChart 
                merchants={data.top_merchants} 
                onReclassify={handleReclassify}
                classifications={classifications}
                isBusiness={false}
              />
            </Card>
          )}
        </Phase>

        {/* Set Budget Targets CTA */}
        <Phase delay={3400} visible={ready}>
          {discCats.length > 0 && (
            <Btn data-testid="set-budgets-cta" onClick={onSetBudgets} secondary style={{ marginBottom: 16 }}>
              SET BUDGET TARGETS FROM THIS ANALYSIS
            </Btn>
          )}
        </Phase>

        <div style={{ height: hasChanges ? 100 : 40 }} />
      </div>
      
      {/* Floating Re-Analyze Button */}
      <FloatingReAnalyzeBtn 
        visible={hasChanges && onReAnalyze}
        onClick={handleReAnalyze}
        loading={reAnalyzing}
      />
      
      {/* Merchant Rule Confirmation Dialog */}
      {confirmDialog && (
        <div data-testid="merchant-rule-confirm-dialog" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }} onClick={() => setConfirmDialog(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#1a1a1a', borderRadius: 16, padding: 28,
            border: '1px solid rgba(255,255,255,0.12)', maxWidth: 380, width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          }}>
            <Mo style={{ fontSize: 9, color: GOLD, letterSpacing: 2, display: 'block', marginBottom: 12 }}>MERCHANT RULE</Mo>
            <Bd style={{ fontSize: 15, color: 'var(--light)', display: 'block', lineHeight: 1.5, marginBottom: 6 }}>
              Apply to all transactions from
            </Bd>
            <Bd style={{ fontSize: 15, color: GOLD, fontWeight: 700, display: 'block', marginBottom: 6 }}>
              {confirmDialog.merchant}?
            </Bd>
            <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginBottom: 20, lineHeight: 1.4 }}>
              {confirmDialog.txnCount} transactions from this merchant will be affected.
            </Mo>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                data-testid="merchant-rule-confirm-btn"
                onClick={() => applyMerchantRule(confirmDialog.merchant, confirmDialog.optionId, confirmDialog.txn)}
                style={{
                  padding: '14px 18px', background: GOLD,
                  border: 'none', borderRadius: 10, cursor: 'pointer',
                  fontFamily: MONO, fontSize: 12, fontWeight: 700, color: '#111', letterSpacing: 0.5,
                }}
              >APPLY TO ALL ({confirmDialog.txnCount})</button>
              <button
                data-testid="individual-classification-btn"
                onClick={() => applyIndividualClassification(confirmDialog.merchant, confirmDialog.optionId)}
                style={{
                  padding: '14px 18px', background: 'var(--surface)',
                  border: '1px solid var(--ghost)', borderRadius: 10, cursor: 'pointer',
                  fontFamily: MONO, fontSize: 12, fontWeight: 600, color: 'var(--light)', letterSpacing: 0.5,
                }}
              >JUST THIS ONE</button>
              <button
                data-testid="merchant-rule-cancel-btn"
                onClick={() => setConfirmDialog(null)}
                style={{
                  padding: '12px 16px', background: 'transparent',
                  border: 'none', borderRadius: 10, cursor: 'pointer',
                  fontFamily: MONO, fontSize: 11, fontWeight: 600, color: 'var(--mute)', letterSpacing: 0.5,
                }}
              >CANCEL</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Quick Fix Modal */}
      {showQuickFix && (
        <QuickFixScreen
          analysisData={data}
          existingClassifications={classifications}
          onClose={() => setShowQuickFix(false)}
          onReAnalyze={handleReAnalyze}
        />
      )}
    </div>
  );
}
